import { useEffect, useId, useRef, useState } from "react";
import { Loader2, Plus, PenLine, Users } from "lucide-react";

// The Floor's single floating "Create" action (2026-10 staging UI pass). A 56px NJM-yellow
// button fixed bottom-right — above the phone bottom nav (safe-area aware), restrained offset
// on desktop. Opens a small labelled menu; each item only hands off to the EXISTING tool the
// parent wires in (PostComposer / eligibility check + CreateCollaborationModal). No business
// logic lives here. The parent hides it while the composer or a collaboration modal is open.
export function FloorCreateFab({ onNewPost, onStartCollaboration, busy = false, hidden = false, resetKey }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const itemRefs = useRef([]);

  const items = [
    { key: "post", label: "New post", hint: "Share a picture, work or a tip", icon: PenLine, onSelect: onNewPost, testid: "floor-fab-new-post" },
    ...(onStartCollaboration
      ? [{ key: "collab", label: "Start a collaboration", hint: "Find crew for a project", icon: Users, onSelect: onStartCollaboration, testid: "floor-fab-start-collaboration" }]
      : []),
  ];

  // Close when the parent hides the button, and on browser back/forward. Switching portal tabs
  // unmounts The Floor (and this menu with it); any in-app link tap is an outside pointerdown.
  useEffect(() => { setOpen(false); }, [hidden, resetKey]);
  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    itemRefs.current[0]?.focus();
    const onPointer = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); buttonRef.current?.focus(); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const list = itemRefs.current.filter(Boolean);
        const i = list.indexOf(document.activeElement);
        const next = e.key === "ArrowDown" ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
        list[next]?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (hidden) return null;

  const select = (item) => { setOpen(false); item.onSelect?.(); };

  return (
    <div ref={rootRef}
      className="fixed z-30 right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] md:right-8 md:bottom-8 flex flex-col items-end gap-3"
      data-testid="floor-create-fab-root">
      {open && (
        <div id={menuId} role="menu" aria-label="Create"
          className="w-64 rounded-2xl border border-hairline bg-paper p-1.5 shadow-[0_12px_32px_-8px_rgba(17,17,17,0.28)]"
          data-testid="floor-create-menu">
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <button key={item.key} type="button" role="menuitem" ref={(el) => { itemRefs.current[i] = el; }}
                onClick={() => select(item)} data-testid={item.testid}
                className="flex w-full min-h-[52px] items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-brand/10 focus:outline-none focus-visible:bg-brand/10 focus-visible:ring-2 focus-visible:ring-ink">
                <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-brand">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block font-mont font-bold text-sm text-ink">{item.label}</span>
                  <span className="block text-xs text-ink-soft">{item.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      <button ref={buttonRef} type="button" aria-label="Create" aria-haspopup="menu" aria-expanded={open ? "true" : "false"}
        aria-controls={open ? menuId : undefined} aria-busy={busy ? "true" : "false"} disabled={busy}
        onClick={() => setOpen((v) => !v)} data-testid="floor-create-fab"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-brand text-ink shadow-[0_8px_20px_-6px_rgba(17,17,17,0.45)] ring-1 ring-ink/10 transition-transform active:scale-95 disabled:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2">
        {busy
          ? <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin" />
          : <Plus aria-hidden="true" className={`h-7 w-7 transition-transform ${open ? "rotate-45" : ""}`} strokeWidth={2.5} />}
      </button>
    </div>
  );
}
